import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Copy, Share, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { toast } from '@/lib/utils/toast';

interface PostPurchaseShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  experienceTitle: string;
  experienceUrl: string;
  experienceImage?: string;
  onBuyMoreTickets?: () => void;
}

export function PostPurchaseShareModal({ 
  isOpen, 
  onClose, 
  experienceTitle, 
  experienceUrl,
  experienceImage,
  onBuyMoreTickets
}: PostPurchaseShareModalProps) {

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(experienceUrl);
      toast.success("Experience link copied to clipboard!");
    } catch (error) {
      // Fallback for browsers that don't support clipboard API
      try {
        const textArea = document.createElement('textarea');
        textArea.value = experienceUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        toast.success("Experience link copied to clipboard!");
      } catch (fallbackError) {
        toast.error("Unable to copy link. Please copy manually: " + experienceUrl);
      }
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: experienceTitle,
          text: `Just secured my spot at ${experienceTitle}! 🎉 This looks incredible - you should join me!`,
          url: experienceUrl,
        });
      } catch (error: any) {
        // User cancelled share dialog or share failed
        if (error.name !== 'AbortError') {
          handleCopyLink(); // Fallback to copy
        }
      }
    } else {
      handleCopyLink(); // Fallback for browsers without native share
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="bg-earth-900 border border-earth-700 rounded-2xl p-8 max-w-md w-full relative shadow-2xl"
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-sand-400 hover:text-sand-200 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>

            {/* Header */}
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <div className="bg-sand-200/10 p-3 rounded-full">
                  <Sparkles className="h-8 w-8 text-sand-300" />
                </div>
              </div>
              <h2 className="text-2xl font-display text-sand-100 mb-2">
                You're Going!
              </h2>
              <p className="text-sand-300">
                Share this amazing experience with friends
              </p>
            </div>

            {/* Experience preview */}
            <div className="bg-earth-800/50 rounded-lg p-4 mb-6">
              <div className="flex gap-4">
                {experienceImage && (
                  <img
                    src={experienceImage}
                    alt={experienceTitle}
                    className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                  />
                )}
                <div className="flex-1">
                  <h3 className="font-medium text-sand-100 mb-1 line-clamp-2">
                    {experienceTitle}
                  </h3>
                  <p className="text-sm text-sand-400">
                    Bring your friends along for the adventure
                  </p>
                </div>
              </div>
            </div>

            {/* Share options */}
            <div className="space-y-4">
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={handleCopyLink}
                  className="flex-1"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Link
                </Button>
                <Button
                  variant="outline"
                  onClick={handleNativeShare}
                  className="flex-1"
                >
                  <Share className="h-4 w-4 mr-2" />
                  Share
                </Button>
              </div>
            </div>

            {/* Buy More Tickets Button */}
            {onBuyMoreTickets && (
              <Button
                onClick={onBuyMoreTickets}
                className="w-full mt-6 bg-sand-200 text-earth-900 hover:bg-sand-100"
              >
                Buy More Tickets
              </Button>
            )}

            {/* Skip option */}
            <div className="text-center mt-6">
              <button
                onClick={onClose}
                className="text-sm text-sand-400 hover:text-sand-300 transition-colors"
              >
                Maybe later
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
} 